"use client";

import { useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { notify } from "@/components/ui/dialogs";
import { can, downloadFile } from "@/utils/service";
import { inputCls, labelCls } from "@/components/finance/Modal";
import DateInput from "@/components/ui/date-input";

const EXPORTS = [
  { entity: "accounts", label: "Accounts", permission: "accounts.export", text: "Companies with type, status, contact details, owner and tags." },
  { entity: "contacts", label: "Contacts", permission: "contacts.export", text: "People with their account, job title and contact details." },
  { entity: "leads", label: "Leads", permission: "leads.export", text: "Leads with source, status, rating and estimated value." },
  { entity: "deals", label: "Deals", permission: "deals.export", text: "Deals with pipeline stage, amount, probability and close date." },
  { entity: "quotes", label: "Quotes", permission: "quotes.export", text: "Quotes and versions with totals and status." },
  { entity: "contracts", label: "Contracts", permission: "contracts.export", text: "Contracts with periods, value, billing cycle and renewal date." },
  { entity: "projects", label: "Projects", permission: "projects.export", text: "Projects with progress, health, dates and budget." },
  { entity: "invoices", label: "Invoices", permission: "invoices.export", text: "Invoices with subtotal, tax, total, paid and balance." },
  { entity: "payments", label: "Payments", permission: "invoices.export", text: "Payments received, method, refunds and unapplied credit." },
  { entity: "expenses", label: "Expenses", permission: "expenses.export", text: "Expenses with category, amounts and approval status." },
];

/** Spreadsheet exports. Each contains only the records you are allowed to see, and every export is logged. */
export default function ReportExports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const available = EXPORTS.filter((e) => can(e.permission));

  const run = async (entity: string) => {
    setBusy(entity);
    try {
      const qs = new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString();
      await downloadFile(`/exports/${entity}${qs ? `?${qs}` : ""}`, `${entity}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      notify("The export didn't work. If it's very large, narrow the date range.");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4">
          <div><label className={labelCls}>Created from</label><DateInput className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label className={labelCls}>Created to</label><DateInput className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <p className="text-xs text-gray-500">Leave empty to export everything. Exports include only records you can see and are recorded in the audit log.</p>
        </div>
        {available.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Your role doesn't include exporting data. Ask an administrator if you need it.</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {available.map((e) => (
              <div key={e.entity} className="flex flex-col rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700"><FileSpreadsheet className="w-5 h-5" /></span><h3 className="text-sm font-semibold text-gray-900">{e.label}</h3></div>
                <p className="mt-3 flex-1 text-xs text-gray-500">{e.text}</p>
                <button onClick={() => run(e.entity)} disabled={busy !== null} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50">
                  <Download className="w-4 h-4" />{busy === e.entity ? "Preparing…" : "Download CSV"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
